import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function BrandPartnershipPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Gloverse — Brand Partnership Deck';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] text-white">
        <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
          <div className="mb-6">
            <span className="inline-block px-3 py-1 text-xs font-semibold tracking-wider uppercase rounded-full bg-white/10 text-white/80 mb-4">Partnership Deck 2026</span>
            <h1 className="text-4xl sm:text-5xl font-black leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Gloverse
            </h1>
            <p className="text-lg sm:text-xl text-white/70 mt-3 leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The beauty social platform where every post is a shoppable product checklist.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 text-center">
              <p className="text-2xl font-bold">9,500+</p>
              <p className="text-xs text-white/60 mt-0.5">Products</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 text-center">
              <p className="text-2xl font-bold">145+</p>
              <p className="text-xs text-white/60 mt-0.5">Brands</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 text-center">
              <p className="text-2xl font-bold">14</p>
              <p className="text-xs text-white/60 mt-0.5">Curated Routines</p>
            </div>
          </div>
        </div>
      </div>

      {/* What is Gloverse */}
      <div className="max-w-3xl mx-auto px-6 py-14">
        <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>What is Gloverse?</h2>
        <p className="text-base text-gray-600 leading-relaxed mb-4">
          Gloverse is a mobile-first social media platform built exclusively for the beauty and skincare community. Unlike traditional social media where product recommendations get buried in captions and DMs, Gloverse makes every piece of content inherently shoppable.
        </p>
        <p className="text-base text-gray-600 leading-relaxed mb-4">
          Every post on Gloverse is paired with a <strong>product checklist</strong> — a curated list of 3-7 products that the creator actually uses. Followers see the exact products, tap to buy, and can follow the same routine with daily check-in streaks.
        </p>
        <p className="text-base text-gray-600 leading-relaxed">
          <strong>For brands, this means every checklist is a native advertisement.</strong> When a creator picks your sunscreen for their "Morning Routine" checklist, every follower who views that post sees your product with a direct buy link. No ad spend. No banner blindness. Just authentic, creator-driven product discovery.
        </p>
      </div>

      {/* Current Features */}
      <div className="bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 py-14">
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>Platform Features</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              {
                title: 'Product Checklists',
                desc: 'Every post comes with a 3-7 product checklist. Each product links directly to its purchase page. Creators curate, followers shop.',
              },
              {
                title: 'Video Routines',
                desc: 'Creators post up to 40-second video tutorials attached to their product checklists. Show the routine, sell the products.',
              },
              {
                title: 'Brand Routines Tab',
                desc: 'A dedicated section featuring expert-curated brand routines. Your brand gets a permanent, always-visible showcase with direct product links.',
              },
              {
                title: 'Brand Profile Pages',
                desc: 'Each brand gets a public profile page (gloverse.in/brand/YourBrand) listing all products, curated routines, and community checklists. Shareable on your website.',
              },
              {
                title: 'Shop by Brand',
                desc: 'A marketplace where users browse all 145+ brands, search products, and add them directly to their checklists. Featured brands get premium placement.',
              },
              {
                title: 'Routine Streaks & Check-ins',
                desc: 'Users follow routines with daily check-ins and build streaks. This drives repeat engagement with your products — not just one-time discovery.',
              },
              {
                title: 'Shareable Checklist Cards',
                desc: 'Beautiful, downloadable PNG cards of any checklist. Creators share them on Instagram Stories, WhatsApp, and other platforms — extending reach beyond Gloverse.',
              },
              {
                title: 'Spotlight & Looks Feed',
                desc: 'Discovery feeds where new users find trending checklists and looks. Your brand\'s products surface organically through creator content.',
              },
            ].map((feature, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{feature.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Brand Value Proposition */}
      <div className="max-w-3xl mx-auto px-6 py-14">
        <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>What Brands Get</h2>
        <p className="text-base text-gray-600 leading-relaxed mb-8">
          A Gloverse brand partnership gives you dedicated real estate inside a platform where every user interaction is a product touchpoint.
        </p>
        <div className="space-y-6">
          {[
            {
              title: 'Curated Brand Routines',
              desc: 'We create expert-curated routines using your products (e.g., "Pigmentation Treatment Routine by Clinderma"). These live permanently in the Brand Routines tab, visible to every user. You control which products are featured and in what order.',
            },
            {
              title: 'Public Brand Profile Page',
              desc: 'A shareable page at gloverse.in/brand/YourBrand that you can embed on your website, link in your Instagram bio, or share with customers. It lists your full product catalog with buy links, curated routines, and community engagement.',
            },
            {
              title: 'Featured Placement in Shop',
              desc: 'Your brand appears in the "Featured Brands" section of the Shop page, above the 145+ brand dropdown. Premium visibility where users come to discover and buy.',
            },
            {
              title: 'Native Product Discovery',
              desc: 'Every time a creator adds your product to their checklist, it becomes a native recommendation. No ads, no banners — just real people sharing real routines with direct buy links to your store.',
            },
            {
              title: 'Cross-promotion',
              desc: 'Gloverse features your brand on our platform; you feature a Gloverse link/banner on your website. Mutual traffic, mutual growth. We provide the banner assets and integration guidance.',
            },
          ].map((item, i) => (
            <div key={i} className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[#1a1a2e] text-white flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-bold">{i + 1}</span>
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Roadmap */}
      <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] text-white">
        <div className="max-w-3xl mx-auto px-6 py-14">
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Product Roadmap</h2>
          <p className="text-sm text-white/60 mb-10">Where Gloverse is heading — and why it matters for brand partners.</p>

          <div className="space-y-10">
            {/* Phase 1 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block px-3 py-1 text-xs font-bold rounded-full bg-green-500/20 text-green-400">LIVE</span>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Phase 1 — Social Commerce Platform</h3>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">
                Product checklists, video routines, brand profiles, shop marketplace, routine streaks, shareable checklist cards, spotlight discovery feeds. All live today.
              </p>
            </div>

            {/* Phase 2 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block px-3 py-1 text-xs font-bold rounded-full bg-yellow-500/20 text-yellow-400">Q3 2026</span>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Phase 2 — Affiliate Commerce & Analytics</h3>
              </div>
              <div className="space-y-3 text-sm text-white/60 leading-relaxed">
                <p>
                  <strong className="text-white/80">Affiliate Earnings for Creators:</strong> Creators earn a commission every time someone buys a product through their checklist link. This incentivizes creators to feature your products — the more they sell, the more they earn.
                </p>
                <p>
                  <strong className="text-white/80">Brand Analytics Dashboard:</strong> Brands get a dashboard showing how many users viewed their products, clicked buy links, and added products to checklists. Real data on ROI from the partnership.
                </p>
                <p>
                  <strong className="text-white/80">For Brands:</strong> With affiliate-driven creators actively promoting your products, you get a performance-based marketing channel. You only pay when products sell. Creators become your sales force.
                </p>
              </div>
            </div>

            {/* Phase 3 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block px-3 py-1 text-xs font-bold rounded-full bg-purple-500/20 text-purple-400">2027</span>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Phase 3 — AI Skin Analysis & Clinic Referrals</h3>
              </div>
              <div className="space-y-3 text-sm text-white/60 leading-relaxed">
                <p>
                  <strong className="text-white/80">AI-Powered Skin Diagnosis:</strong> Users upload a photo and Gloverse's AI analyzes their skin condition — acne severity, pigmentation, dryness, signs of concern — with clinical-grade accuracy.
                </p>
                <p>
                  <strong className="text-white/80">Smart Recommendations Based on Severity:</strong>
                </p>
                <div className="ml-4 space-y-2">
                  <p><strong className="text-green-400">Score 0-20 (Mild):</strong> AI recommends specific products and routines from partner brands. "Try this hydrating routine by Cetaphil."</p>
                  <p><strong className="text-yellow-400">Score 20-60 (Moderate):</strong> AI recommends clinical treatments at partner clinics. "Consider a chemical peel at Clinderma for faster results."</p>
                  <p><strong className="text-red-400">Score 60+ (Severe):</strong> AI recommends consulting a dermatologist. "We recommend seeing Dr. Priyanka Reddy at DNA Skin Clinic for a professional assessment."</p>
                </div>
                <p>
                  <strong className="text-white/80">For Brands & Clinics:</strong> This creates a direct patient/customer funnel. Skincare brands get recommended to users with mild issues. Clinics get referrals for moderate-to-severe cases. Dermatologists get connected to patients who need professional help. Gloverse becomes the full-stack skincare platform — from discovery to diagnosis to treatment to products.
                </p>
              </div>
            </div>

            {/* Phase 4 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block px-3 py-1 text-xs font-bold rounded-full bg-blue-500/20 text-blue-400">2027+</span>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Phase 4 — Native Mobile App & Scale</h3>
              </div>
              <div className="space-y-3 text-sm text-white/60 leading-relaxed">
                <p>
                  <strong className="text-white/80">iOS & Android App:</strong> Native mobile app on the App Store and Google Play for push notifications, camera integration, and offline access.
                </p>
                <p>
                  <strong className="text-white/80">Personalized "For You" Feed:</strong> AI-powered feed that shows users checklists and products tailored to their skin type, concerns, and browsing history. Your products surface to the right users automatically.
                </p>
                <p>
                  <strong className="text-white/80">Group Routines & Challenges:</strong> Users create group skincare challenges (e.g., "30-day Glow Up with Minimalist"). Brands can sponsor challenges for massive organic reach.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* The Opportunity */}
      <div className="max-w-3xl mx-auto px-6 py-14">
        <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>The Opportunity</h2>
        <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 border border-gray-100">
          <p className="text-base text-gray-600 leading-relaxed mb-4">
            India's beauty and personal care market is projected to reach <strong>$30 billion by 2027</strong>. Social commerce is the fastest-growing channel, with 75% of Gen-Z discovering beauty products through social media.
          </p>
          <p className="text-base text-gray-600 leading-relaxed mb-4">
            But there's no dedicated platform that combines <strong>social content + product discovery + shoppable links + skincare diagnostics</strong> in one place. Instagram has content but no native commerce. Nykaa has commerce but no social. Gloverse bridges the gap.
          </p>
          <p className="text-base text-gray-600 leading-relaxed">
            As a founding brand partner, you get first-mover advantage: premium placement, custom routines, and a direct channel to engaged beauty consumers — before the platform scales to millions.
          </p>
        </div>
      </div>

      {/* Partnership Models */}
      <div className="bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 py-14">
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>Partnership Models</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Cross-Promotion</h3>
              <p className="text-xs text-gray-400 mb-3">Mutual visibility exchange</p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li>Gloverse features your brand routines & shop placement</li>
                <li>You feature a Gloverse section/banner on your website</li>
                <li>Shared audience growth with zero cost</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Featured Brand</h3>
              <p className="text-xs text-gray-400 mb-3">Premium placement & custom content</p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li>Custom brand routines curated by our team</li>
                <li>Top placement in Brand Routines & Shop tabs</li>
                <li>Public brand profile page with full catalog</li>
                <li>Priority in AI skin recommendations (Phase 3)</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Affiliate Commerce</h3>
              <p className="text-xs text-gray-400 mb-3">Coming Q3 2026</p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li>Tracked affiliate links for every product</li>
                <li>Creators earn commissions on sales they drive</li>
                <li>Brand analytics dashboard for ROI tracking</li>
                <li>Performance-based — pay only for results</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Clinic Referrals</h3>
              <p className="text-xs text-gray-400 mb-3">Coming 2027</p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li>AI skin analysis recommends your clinic for moderate-severe cases</li>
                <li>Direct patient funnel from diagnosis to consultation</li>
                <li>Track referral conversions</li>
                <li>Exclusive for dermatology clinic partners</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-3xl mx-auto px-6 py-14 text-center">
        <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to partner?</h2>
        <p className="text-base text-gray-500 mb-6 max-w-xl mx-auto">
          Join 145+ brands already on Gloverse. Let's build something that drives real engagement and real sales for your products.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="https://gloverse.in/shop"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-[#1a1a2e] text-white text-sm font-semibold hover:bg-[#16213e] transition-colors"
          >
            Explore the Platform
          </a>
          <a
            href="mailto:info@gloverse.in"
            className="inline-flex items-center justify-center px-6 py-3 rounded-full border-2 border-[#1a1a2e] text-[#1a1a2e] text-sm font-semibold hover:bg-gray-50 transition-colors"
          >
            Get in Touch
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 py-8">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Gloverse</p>
          <p className="text-xs text-gray-400">gloverse.in — The beauty platform where every checklist is a product recommendation.</p>
        </div>
      </div>
    </div>
  );
}
